import { toast } from "react-toastify";
import axios from "axios";
import produce from "immer";
import { v4 as uuidv4 } from "uuid";
import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { fetchAuthToken } from "app/Auth";
import { USER_RESTRICTED_CODE } from "app/types";
import i18n from "common/i18n";
import { client } from "common/apiClient";
import { ComponentTypeIds, ComponentManager, ComponentCategory } from "common/components";
import { actions } from "features/creator/slice";
import { SCREEN_SIZE, DEFAULT_SCREEN_ORIENTATION } from "features/creator/constants";
import {
  ProjectType,
  CanvasEntity,
  ProjectEntity,
  BlocklyEntity,
  ScreensEntity,
  PropertiesEntity,
  ScreenOrientation,
  PickupResponse,
  ComponentsEntityData,
  ProjectVersionReason,
  ProjectEditHistoryEntry,
  ProjectVersionsResponse,
  CreatorContentSnapshot,
} from "features/creator/types";

export const getAppProjects = createAsyncThunk<
  ProjectEntity[],
  {
    uid: string;
    project_types?: ProjectType[];
  }
>("creator/getAppProjects", async ({ uid, project_types }) => {
  const token = await fetchAuthToken();
  const response = await client({
    token: token,
  }).get(
    `/v1/projects/user/${uid}${
      !!project_types && project_types.length > 0 ? `?project_type=${project_types.join(",")}` : ""
    }`,
  );
  return response.data;
});

export const createScreen = (
  orientation: ScreenOrientation,
  name: string,
  order: number,
): { screen: ScreensEntity; screenProperty: PropertiesEntity } => {
  const screenId = uuidv4();
  const screenTypeId = ComponentTypeIds.SCREEN;
  const screenCategoryId = ComponentCategory.ACTION;
  const screen: ScreensEntity = {
    id: screenId,
    name: name,
    order: order,
    children: [],
    childrenOrder: [],
  };
  const defaultProperty = ComponentManager[screenTypeId].component.property;
  const property = produce(defaultProperty, (draft) => {
    draft.name = name;
    draft.defaultName = name;
    draft.screenshot = "";
    draft.style.layout.width = SCREEN_SIZE[orientation].WIDTH;
    draft.style.layout.height = SCREEN_SIZE[orientation].HEIGHT;
  });
  const screenProperty: PropertiesEntity = {
    id: screenId,
    typeId: screenTypeId,
    categoryId: screenCategoryId,
    screenId: screenId,
    property: property,
  };
  return { screen, screenProperty };
};

export const createProject = createAsyncThunk<
  { project: ProjectEntity },
  {
    uid: string;
    name: string;
    type: ProjectType;
    tags?: string;
  }
>("creator/createProject", async ({ uid, name, type, tags }, { rejectWithValue }) => {
  const screenTypeId = "screen";
  const { screen, screenProperty } = createScreen(
    DEFAULT_SCREEN_ORIENTATION,
    `${i18n.t("MSG_CREATOR_COMPONENT_NAME_SCREEN")}1`,
    0,
  );
  const components: ComponentsEntityData = Array.from(Object.values(ComponentManager)).reduce(
    (acc, component) =>
      Object.assign(
        acc,
        component.id === screenTypeId
          ? {
              [component.id]: [{ id: screen.id, deleted: false }],
            }
          : { [component.id]: [] },
      ),
    {},
  );
  const canvas: CanvasEntity = { id: screen.id, x: 0, y: 0 };
  const blockly: BlocklyEntity = {
    screenId: screen.id,
    xmlText: '<xml xmlns="https://developers.google.com/blockly/xml"></xml>',
  };

  try {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).post(`/v1/projects/user/${uid}`, {
      type,
      tags,
      name,
      content: {
        selectedScreenId: screen.id,
        defaultScreenId: screen.id,
        screenSize: {
          width: SCREEN_SIZE[DEFAULT_SCREEN_ORIENTATION].WIDTH,
          height: SCREEN_SIZE[DEFAULT_SCREEN_ORIENTATION].HEIGHT,
          orientation: DEFAULT_SCREEN_ORIENTATION,
        },
        canvas: [canvas],
        screens: [screen], // {id: String, name: String, typeId: String, children: Array}
        components: [{ screenId: screen.id, data: components }], // [{screenId: String, data: [{componentTypeId:[componentId]}]]
        properties: [screenProperty], // [{id(componentId): String, screenId: String, property: Object}]
        blockly: [blockly],
      },
    });
    return { ...response.data.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return rejectWithValue({
        code: error.response.data?.code || "QUOTA_EXCEEDED",
        message: error.response.data?.message || i18n.t("MSG_TOAST_PROJECT_QUOTA_EXCEEDED"),
      });
    }
    throw error;
  }
});

export const copyProject = createAsyncThunk<
  { project: ProjectEntity },
  {
    uid: string;
    copied_project_id: string;
    name: string;
  }
>("creator/copyProject", async ({ uid, copied_project_id, name }, { rejectWithValue }) => {
  try {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).post(`/v1/projects/user/${uid}/copy/${copied_project_id}`, { name });
    return { ...response.data.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return rejectWithValue({
        code: error.response.data?.code || "QUOTA_EXCEEDED",
        message: error.response.data?.message || i18n.t("MSG_TOAST_PROJECT_QUOTA_EXCEEDED"),
      });
    }
    throw error;
  }
});

export const getUserProjectContent = createAsyncThunk(
  "creator/getUserProjectContent",
  async ({ project_id }: { project_id: string }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).get(`/v1/projects/user/content/${project_id}`);
    return { ...response.data.data };
  },
);

/** エディタの中身だけを取る（restore・へんしゅうを すてる のあと。エディタを開き直さずに差し替えるため） */
export const fetchProjectContent = async ({
  project_id,
}: {
  project_id: string;
}): Promise<{ content: Omit<CreatorContentSnapshot, "selectedScreenId">; has_draft: boolean }> => {
  const token = await fetchAuthToken();
  const response = await client({ token }).get(`/v1/projects/user/content/${project_id}`);
  const content = response.data.data.content;
  // 保存した中身は { id: 部品 } の形（buildContent）。エディタの差し替えでは配列にそろえる
  return {
    content: {
      defaultScreenId: content.defaultScreenId,
      screenSize: content.screenSize,
      canvas: Object.values(content.canvas),
      screens: Object.values(content.screens),
      components: Object.values(content.components),
      properties: Object.values(content.properties),
      blockly: Object.values(content.blockly),
    },
    has_draft: !!response.data.data.has_draft,
  };
};

export const updateProject = createAsyncThunk(
  "creator/updateProject",
  async ({
    uid,
    project_id,
    project,
  }: {
    uid: string;
    project_id: string;
    project: ProjectEntity;
  }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).put(`/v1/projects/user/${uid}/meta/${project_id}`, project);
    return { ...response.data.data };
  },
);

export const updateProjectCapture = async ({
  uid,
  project_id,
  project,
}: {
  uid: string;
  project_id: string;
  project: ProjectEntity;
}) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  }).put(`/v1/projects/user/${uid}/meta/${project_id}`, project);
};

/** 保存する中身（エディタの今の状態） */
const buildContent = (creator: RootState["creator"]) => ({
  defaultScreenId: creator.defaultScreenId,
  screenSize: creator.screenSize,
  canvas: creator.canvas.entities,
  screens: creator.screens.entities,
  components: creator.components.entities,
  properties: creator.properties.entities,
  blockly: creator.blockly.entities,
});

/**
 * 中身を保存する。project_id は呼んだ時点のもので固定し、いまエディタで開いているのが別のアプリなら書かない
 * （以前は保存が走った時点の editingProjectId を読んでいたので、アプリを切り替えた直後に別のアプリへ書くおそれがあった）
 */
const saveContent = async (
  uid: string,
  projectId: string,
  getState: () => unknown,
  dispatch: (action: unknown) => unknown,
) => {
  const { creator } = getState() as RootState;
  if (!projectId || creator.editingProjectId !== projectId) return;
  const token = await fetchAuthToken();
  try {
    const response = await client({
      token: token,
    }).put(`/v1/projects/user/${uid}/content/${projectId}`, { content: buildContent(creator) });
    // 公開中のアプリの編集はドラフトに入る。「こうかいを こうしん」を押せるようにする
    if (response.data?.has_draft !== undefined) {
      dispatch(
        actions.updateProjectDraftState({
          project_id: projectId,
          has_draft: response.data.has_draft,
        }),
      );
    }
  } catch (error) {
    console.error(JSON.stringify(error));
    // 保存できなかったことを伝える（以前は何も出ず、閉じると変更が消えていた）。続けて失敗しても 1 つだけ出す
    toast.warn(i18n.t("MSG_CREATOR_TOAST_SAVE_FAILED"), { toastId: "creator-save-failed" });
  }
};

/**
 * 保存は 1 本ずつ順番に送る。以前は間引き中の保存と「うごかす」の即時保存が並んで飛び、
 * 古い中身の要求があとから届くと新しい中身を上書きすることがあった
 */
let saveQueue: Promise<void> = Promise.resolve();
const saveContentInOrder = (...args: Parameters<typeof saveContent>) => {
  saveQueue = saveQueue.then(() => saveContent(...args)).catch(() => undefined);
  return saveQueue;
};

const throttledSaveContent = throttle(saveContentInOrder, 5000);

/** 待っている保存をすぐに流す。エディタを閉じる・こうかいを こうしん・うごかす の前に呼ぶ */
export const flushProjectContent = async () => {
  await throttledSaveContent.flush();
};

export const updateProjectContentImmediately = createAsyncThunk(
  "creator/updateProjectContentImmediately",
  async ({ uid }: { uid: string }, thunkAPI) => {
    const { creator } = thunkAPI.getState() as RootState;
    // 待っている保存を先に流し、そのあと今の中身で保存する
    await flushProjectContent();
    await saveContentInOrder(uid, creator.editingProjectId, thunkAPI.getState, thunkAPI.dispatch);
  },
);

export const updateProjectContent = createAsyncThunk(
  "creator/updateProjectContent",
  async ({ uid }: { uid: string }, thunkAPI) => {
    const { creator } = thunkAPI.getState() as RootState;
    throttledSaveContent(uid, creator.editingProjectId, thunkAPI.getState, thunkAPI.dispatch);
  },
);

/** こうかいを こうしん。公開中のアプリのドラフトがみんなに見える版になり、版が積まれる */
export const completeProject = createAsyncThunk(
  "creator/completeProject",
  async ({ uid, project_id }: { uid: string; project_id: string }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).post(`/v1/projects/user/${uid}/complete/${project_id}`);
    return response.data as {
      project_id: string;
      completed_version: number;
      has_draft: boolean;
    };
  },
);

/** エディタを閉じた・開いたとき・10 分ごとの版を残す（同じ中身なら api が積まない）。保存ボタンの版は必ず残る */
export const createProjectVersion = async ({
  uid,
  project_id,
  reason = ProjectVersionReason.CLOSE,
  name,
}: {
  uid: string;
  project_id: string;
  reason?:
    | ProjectVersionReason.CLOSE
    | ProjectVersionReason.OPEN
    | ProjectVersionReason.AUTO
    | ProjectVersionReason.SAVE;
  /** 「名前をつけて保存」でつけた名前（reason が SAVE のときだけ） */
  name?: string;
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).post(`/v1/projects/user/${uid}/versions/${project_id}`, {
    reason,
    name,
  });
};

/** 版の一覧と変更履歴（新しい順。見られる件数はプランで決まる） */
export const getProjectVersions = async ({
  uid,
  project_id,
}: {
  uid: string;
  project_id: string;
}) => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get(
    `/v1/projects/user/${uid}/versions/${project_id}`,
  );
  return response.data as ProjectVersionsResponse;
};

/** この版に もどす（公開中ならドラフトへ。みんなに見える版は変わらない） */
export const restoreProjectVersion = async ({
  uid,
  project_id,
  version_id,
}: {
  uid: string;
  project_id: string;
  version_id: string;
}) => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).post(
    `/v1/projects/user/${uid}/versions/${project_id}/${version_id}/restore`,
  );
  return response.data as { project_id: string; has_draft: boolean };
};

/** 変更履歴をまとめて送る */
export const appendProjectEditHistory = async ({
  uid,
  project_id,
  entries,
}: {
  uid: string;
  project_id: string;
  entries: Omit<ProjectEditHistoryEntry, "id" | "created_at">[];
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).post(`/v1/projects/user/${uid}/history/${project_id}`, {
    entries,
  });
};

export const removeProject = createAsyncThunk(
  "creator/removeProject",
  async ({ uid, project_id }: { uid: string; project_id: string }) => {
    const token = await fetchAuthToken();
    const response = await client({
      token: token,
    }).delete(`/v1/projects/user/${uid}/${project_id}`);
    return { project_id, deleted_at: response.data.deleted_at };
  },
);

export const deleteProject = createAsyncThunk(
  "creator/deleteProject",
  async ({ uid, project_id }: { uid: string; project_id: string }) => {
    const token = await fetchAuthToken();
    await client({
      token: token,
    }).delete(`/v1/projects/user/${uid}/${project_id}/completely`);
    return project_id;
  },
);

export const restoreProject = createAsyncThunk(
  "creator/restoreProject",
  async ({ uid, project_id }: { uid: string; project_id: string }, { rejectWithValue }) => {
    try {
      const token = await fetchAuthToken();
      await client({
        token: token,
      }).put(`/v1/projects/user/${uid}/restore/${project_id}`);
      return project_id;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return rejectWithValue({
          code: error.response.data?.code || "QUOTA_EXCEEDED",
          message: error.response.data?.message || i18n.t("MSG_TOAST_PROJECT_QUOTA_EXCEEDED"),
        });
      }
      throw error;
    }
  },
);

export const publishProject = createAsyncThunk(
  "creator/publishProject",
  async (
    {
      uid,
      project_id,
      enable_copied,
    }: {
      uid: string;
      project_id: string;
      enable_copied: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const token = await fetchAuthToken();
      const response = await client({
        token: token,
      }).post(`/v1/projects/published/${uid}/${project_id}`, { enable_copied });
      return response.data;
    } catch (error) {
      // 運営が公開を止めている（画面を開いたあとにかけられた等）。呼び出し側で理由を出す
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 403 &&
        error.response.data?.code === USER_RESTRICTED_CODE
      ) {
        return rejectWithValue({ code: USER_RESTRICTED_CODE });
      }
      throw error;
    }
  },
);

export const stopPublishProject = createAsyncThunk(
  "creator/stopPublishProject",
  async ({ uid, project_id }: { uid: string; project_id: string }) => {
    const token = await fetchAuthToken();
    await client({
      token: token,
    }).delete(`/v1/projects/published/${uid}/${project_id}`);
    return project_id;
  },
);

export const favoriteProject = async ({ uid, project_id }: { uid: string; project_id: string }) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  }).post(`/v1/projects/favorited/${uid}/${project_id}`);
};

export const stopFavoriteProject = async ({
  uid,
  project_id,
}: {
  uid: string;
  project_id: string;
}) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  }).delete(`/v1/projects/favorited/${uid}/${project_id}`);
};

export const getProjectRanking = async ({ project_types }: { project_types?: ProjectType[] }) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  })
    .get(
      `/v1/ranking/projects/by/favorited${
        !!project_types && project_types.length > 0
          ? `?project_type=${project_types.join(",")}`
          : ""
      }`,
    )
    .then((res) => res.data.data);
};

export const getUserRanking = async ({ project_types }: { project_types?: ProjectType[] }) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  })
    .get(
      `/v1/ranking/user/by/viewed${
        !!project_types && project_types.length > 0
          ? `?project_type=${project_types.join(",")}`
          : ""
      }`,
    )
    .then((res) => res.data.data);
};

export const getUserRankingProjects = async ({
  uid,
  project_types,
}: {
  uid: string;
  project_types?: ProjectType[];
}) => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  }).get(
    `/v1/ranking/user/${uid}/projects${
      !!project_types && project_types.length > 0 ? `?project_type=${project_types.join(",")}` : ""
    }`,
  );
};

export const getPickupProject = async (): Promise<PickupResponse> => {
  const token = await fetchAuthToken();
  return client({
    token: token,
  })
    .get("/v1/pickup/projects")
    .then((res) => res.data.data);
};

/** アプリの通報。ゲストの 403 は再送しても変わらないのでリトライしない */
export const reportProject = async (params: {
  project_id: string;
  uid: string;
  reason: string;
  detail?: string;
}): Promise<void> => {
  const token = await fetchAuthToken();
  await client({ token }).post(
    `/v1/projects/${params.project_id}/report`,
    { uid: params.uid, reason: params.reason, detail: params.detail },
    { "axios-retry": { retries: 0 } },
  );
};
